import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrossChainRoute } from '@/lib/query-options';
import MingcuteCheckCircleLine from '~icons/mingcute/check-circle-line';
import MingcuteCloseCircleLine from '~icons/mingcute/close-circle-line';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Progress } from '../ui/progress';
import { TransferInfo } from './transfer-info';

interface TransferProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiver: string;
  fromAddress: string;
  fromAmount: string;
  toAmount: string;
  route: CrossChainRoute;
  progress: number;
  status: 'pending' | 'active' | 'completed' | 'failed';
  error?: string;
  result?: {
    txHash?: string;
    intentId?: string;
    requestId?: string;
  };
}

export function TransferProgressDialog({
  open,
  onOpenChange,
  receiver,
  fromAddress,
  fromAmount,
  toAmount,
  route,
  progress,
  status,
  error,
  result,
}: TransferProgressDialogProps) {
  const { t } = useTranslation();
  const isCompleted = useMemo(() => status === 'completed', [status]);
  const isFailed = useMemo(() => status === 'failed', [status]);
  const isProcessing = useMemo(
    () => status === 'pending' || status === 'active',
    [status],
  );

  const handleOpenChange = useCallback(
    (value: boolean) => {
      // Only allow closing when completed or failed
      if (!value && !isProcessing) {
        onOpenChange(value);
      }
    },
    [isProcessing, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCompleted && (
              <MingcuteCheckCircleLine className="size-5 text-green-500" />
            )}
            {isFailed && (
              <MingcuteCloseCircleLine className="size-5 text-destructive" />
            )}
            {isProcessing && <Spinner className="size-5" />}
            {isCompleted
              ? t('transfer.transferSubmitted')
              : isFailed
                ? t('transfer.transferFailed')
                : t('transfer.processingTransfer')}
          </DialogTitle>
          <DialogDescription>
            {isCompleted
              ? t('transfer.transferSubmittedSuccess')
              : isFailed
                ? t('transfer.transferError')
                : t('transfer.pleaseWait')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('transfer.progress')}</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>

          <TransferInfo
            receiver={receiver}
            fromAddress={fromAddress}
            amount={fromAmount}
            recive={toAmount}
            route={route}
          />

          {result?.txHash && (
            <div className="rounded-lg border p-3 space-y-1">
              <div className="text-sm font-medium">{t('transfer.transactionHash')}</div>
              <div className="text-xs text-muted-foreground break-all font-mono">
                {result.txHash}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
              <div className="text-sm font-medium text-destructive">{t('transfer.error')}</div>
              <div className="text-xs text-destructive/80 mt-1">{error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          {!isProcessing && (
            <Button onClick={() => handleOpenChange(false)} className="w-full">
              {t('common.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


